import React, { useContext, useMemo } from 'react';
import {
  CalendarOutlined,
  DollarCircleOutlined,
  HistoryOutlined,
  SyncOutlined,
  FieldNumberOutlined,
  DiffOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import { Col, Row, Space } from 'antd';
import StatCard from '@components/StatCard';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import { ApplicationsConstants } from '@utils/applicationsConstants';

interface IInvoiceStats {
  data: {
    value: string | 0;
    expirationDate: string;
    totalTraffic: string | 0;
    period: string;
    number: number;
    terminals?: string | number;
    chips?: string;
  };
}

const InvoiceStats: React.FC<IInvoiceStats> = ({ data }) => {
  const { t } = useTranslation('invoices');
  const { selectedApp } = useContext(SessionContext);

  const allStats = useMemo(
    () => [
      {
        key: 'first-column',
        stats: [
          {
            name: 'value',
            icon: <DollarCircleOutlined />,
            value: data.value,
          },
          {
            name: 'expirationDate',
            icon: <CalendarOutlined />,
            value: data.expirationDate,
          },
        ],
      },
      {
        key: 'second-column',
        stats: [
          {
            name: 'totalTraffic',
            icon: <SyncOutlined />,
            value: data.totalTraffic ?? '-',
          },
          selectedApp === ApplicationsConstants.MULTI_CONNECT
            ? {
                name: 'chips',
                icon: <DiffOutlined />,
                value: data.chips,
              }
            : {
                name: 'terminals',
                icon: <CodeOutlined />,
                value: data.terminals,
              },
        ],
      },
      {
        key: 'third-column',
        stats: [
          {
            name: 'number',
            icon: <FieldNumberOutlined />,
            value: data?.number ? `${data?.number?.toString()} ` : '-',
          },
          {
            name: 'period',
            icon: <HistoryOutlined />,
            value: data.period ?? '-',
          },
        ],
      },
    ],
    [
      data.chips,
      data.expirationDate,
      data.number,
      data.period,
      data.terminals,
      data.totalTraffic,
      data.value,
      selectedApp,
    ],
  );

  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
      {allStats?.map(({ key, stats }) => (
        <Col key={key} lg={12} md={12} xl={8} xs={24}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {stats?.map(({ name, icon, value }) => (
              <StatCard
                key={name}
                title={t(`invoice.${name}`)}
                icon={icon}
                value={value ?? ''}
                loading={!value && value !== 0}
              />
            ))}
          </Space>
        </Col>
      ))}
    </Row>
  );
};

export default InvoiceStats;
